import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AccountDialog } from "@/components/crm/AccountDialog";
import { RecordDrawer } from "@/components/crm/RecordDrawer";
import { SalesShell } from "@/components/crm/SalesShell";
import { SalesActions } from "@/components/crm/SalesActions";
import { BulkBar, SelectBox, useRowSelection } from "@/components/crm/BulkBar";
import { EmptyState, RenewalBadge } from "@/components/crm/parts";
import { childrenOf, groupRollUp, hierarchyRows } from "@/lib/crm/hierarchy";
import { useOrgMembers } from "@/lib/crm/useOrgMembers";
import { accountSheets } from "@/lib/exports/crm";
import { accountVisual } from "@/lib/crm/visual-sources";
import { useSalesOwner } from "@/lib/crm/owner-filter";
import {
  useAccounts,
  useContracts,
  useDeleteRecord,
  useOpportunities,
  useStages,
} from "@/lib/crm/useCrm";
import {
  ACCOUNT_STATUSES,
  gbp,
  isLiveContract,
  isOpen,
  labelOf,
  money,
  weightedValue,
  type Account,
} from "@/lib/crm/types";

export const Route = createFileRoute("/_authenticated/sales/accounts")({
  head: () => ({
    meta: [
      { title: "Accounts — MyPriorities" },
      {
        name: "description",
        content:
          "Customer and prospect accounts with contracted revenue, live opportunities and the next renewal in view.",
      },
      { property: "og:title", content: "Accounts — MyPriorities" },
      {
        property: "og:description",
        content: "One page per customer: revenue, deals, notes and activity.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: AccountsPage,
});

function AccountsPage() {
  return (
    <SalesShell
      section="customers"
      title="Accounts"
      subtitle="Customers and prospects, with revenue and open opportunities attached."
    >

      {({ orgId, userId }) => <Accounts orgId={orgId} userId={userId} />}
    </SalesShell>
  );
}

function Accounts({ orgId, userId }: { orgId: string; userId: string }) {
  const accounts = useAccounts(orgId);
  const contracts = useContracts(orgId);
  const opportunities = useOpportunities(orgId);
  const stages = useStages(orgId);
  const { ownerLabel, ownerOptions } = useOrgMembers(orgId);
  const remove = useDeleteRecord("crm_accounts");
  const selection = useRowSelection();

  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [detail, setDetail] = useState<Account | null>(null);

  const { scope } = useSalesOwner();
  const stageRows = stages.data ?? [];
  const scopedAccounts = scope(accounts.data ?? []);

  // Indented tree so sub-brands and separate SLAs sit under their parent group.
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = scopedAccounts.filter(
      (a) => !q || a.name.toLowerCase().includes(q) || (a.industry ?? "").toLowerCase().includes(q),
    );
    return hierarchyRows(matches);
  }, [scopedAccounts, search]);

  const summary = (accountId: string) => {
    const mine = (contracts.data ?? []).filter((c) => c.account_id === accountId && isLiveContract(c));
    const deals = (opportunities.data ?? []).filter(
      (o) => o.account_id === accountId && isOpen(o, stageRows),
    );
    const nextRenewal = mine
      .map((c) => c.end_date)
      .filter((d): d is string => Boolean(d))
      .sort()[0];
    return {
      revenue: mine.reduce((s, c) => s + money(c.annual_value), 0),
      contracts: mine.length,
      dealCount: deals.length,
      weighted: deals.reduce((s, o) => s + weightedValue(o, stageRows), 0),
      nextRenewal: nextRenewal ?? null,
    };
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap gap-2">
        <Input
          className="max-w-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search accounts"
        />
        <SalesActions
          exports={[
            {
              label: "Accounts",
              baseName: "accounts",
              sheets: () =>
                accountSheets({
                  accounts: scopedAccounts,
                  contracts: contracts.data ?? [],
                  opportunities: opportunities.data ?? [],
                  stages: stageRows,
                  ownerLabel,
                }),
            },
          ]}
          visual={() =>
            accountVisual(scopedAccounts, contracts.data ?? [], opportunities.data ?? [], stageRows)
          }
          sourceNote="From your account list"
        />
        <Button
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="size-4" /> New account
        </Button>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="No accounts yet"
          body="Add your first customer or prospect — contracts, contacts and opportunities all hang off it."
        />
      ) : (
        <>
        <BulkBar
          table="crm_accounts"
          noun="account"
          ids={rows.map(({ account }) => account.id)}
          selected={selection.selected}
          onReplace={selection.replace}
          onClear={selection.clear}
          blockedHint="They still have contracts, contacts or opportunities attached."
        />
        <ul className="grid gap-2">
          {rows.map(({ account: a, depth }) => {
            const s = summary(a.id);
            const kids = childrenOf(accounts.data ?? [], a.id);
            const group = kids.length
              ? groupRollUp({
                  accounts: accounts.data ?? [],
                  contracts: contracts.data ?? [],
                  opportunities: opportunities.data ?? [],
                  stages: stageRows,
                  rootId: a.id,
                })
              : null;
            return (
              <li
                key={a.id}
                className="rounded-lg border border-border bg-card p-3"
                style={depth ? { marginLeft: `${Math.min(depth, 4) * 1.25}rem` } : undefined}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex min-w-0 items-start gap-3">
                    <SelectBox
                      checked={selection.isSelected(a.id)}
                      onChange={() => selection.toggle(a.id)}
                      label={`Select ${a.name}`}
                    />
                  <div className="min-w-0">
                    <button
                      type="button"
                      className="text-left text-sm font-semibold hover:underline"
                      onClick={() => setDetail(a)}
                    >
                      {a.name}
                    </button>
                    <p className="text-[11px] text-muted-foreground">
                      {labelOf(ACCOUNT_STATUSES, a.status)}
                      {a.industry ? ` · ${a.industry}` : ""} · Owner {ownerLabel(a.owner_user_id)}
                      {depth ? " · sub-brand" : ""}
                    </p>
                    {group ? (
                      <p className="text-[11px] text-muted-foreground">
                        Group of {group.accountCount}: {gbp(group.annualValue)} p.a. ·{" "}
                        {group.contractCount} contracts · {gbp(group.weightedPipeline)} weighted
                        pipeline
                      </p>
                    ) : null}
                   </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">
                      {gbp(s.revenue)} · {s.contracts} contracts
                    </Badge>
                    <Badge variant="secondary">
                      {s.dealCount} open · {gbp(s.weighted)} weighted
                    </Badge>
                    {s.nextRenewal ? <RenewalBadge endDate={s.nextRenewal} /> : null}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditing(a);
                        setDialogOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Delete account"
                      onClick={() =>
                        void remove
                          .mutateAsync(a.id)
                          .then(() => toast.success("Account deleted"))
                          .catch(() =>
                            toast.error(
                              "That account still has contracts or opportunities — remove them first.",
                            ),
                          )
                      }
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        </>
      )}

      <AccountDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        orgId={orgId}
        userId={userId}
        account={editing}
        ownerOptions={ownerOptions}
      />

      <RecordDrawer
        open={Boolean(detail)}
        onOpenChange={(v) => !v && setDetail(null)}
        orgId={orgId}
        userId={userId}
        kind="account"
        recordId={detail?.id ?? null}
        title={detail?.name ?? ""}
        subtitle={detail ? labelOf(ACCOUNT_STATUSES, detail.status) : undefined}
      />
    </div>
  );
}
